import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CreateMapDetails from "./CreateMapDetails";
import { MapsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/mapsEndpointLogic";
import { useEffect, useState } from "react";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setOriginalParams,
  setSelectedDestinationTableIDMaps,
  setSelectedInterfaceIDMaps,
} from "../../../../../features/mapsSlice";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";

const tableHeadings = {
  destination_table_column: "Destination Table Column",
  source_field_expression: "Source Field Expression",
  calculation_type: "Calculation Type ",
  unique_definition: "Unique Definition",
};

const CreateMap = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const [loading, setLoading] = useState(false);
  const [mapName, setMapName] = useState("");
  const [navigatingBack] = useState(false);
  const [selectedInterface, setSelectedInterface] = useState(null);
  const [selectedDestinationTable, setSelectedDestinationTable] =
    useState(null);

  const [isCreating, setIsCreating] = useState(false);
  const interfaceData = useSelector((state: any) => state.maps.interfaceData);
  const destinationTablesData = useSelector(
    (state: any) => state.maps.destinationTablesData
  );
  const originalParams = useSelector((state: any) => state.maps.originalParams);
  const {
    selectedInterfaceIDMaps,
    selectedDestinationTableIDMaps,
    mapDetailsDataPayload,
    isValidPayloadDetails,
  } = useSelector((state: any) => state.maps);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          MapsEndpointLogic.getInterface(oauthTokenHeader),
          MapsEndpointLogic.getDestinationTables(oauthTokenHeader),
          MapsEndpointLogic.getAllCalculationTypesMaps(oauthTokenHeader),
          MapsEndpointLogic.getUnifierUniqueDefinitionMaps(oauthTokenHeader),
        ]);
      } catch (error: any) {
        dispatch(
          setDisplayErrorSnackBar({
            message: error?.parsedText || "Unknown error occurred",
            type: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, oauthTokenHeader]);

  const handleCreateMap = async () => {
    setIsCreating(true);
    try {
      const payload = {
        map_name: mapName.trim(),
        backend_interface_id: selectedInterfaceIDMaps,
        destination_table_id: selectedDestinationTableIDMaps,
        field_mapping: mapDetailsDataPayload,
      };
      const response = await MapsEndpointLogic.createMap(
        oauthTokenHeader,
        payload
      );
      if (response.status === 201) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Map created successfully",
            type: "success",
          })
        );
        navigate(`/data_collection/unifier/maps?${originalParams}`, {
          replace: true,
        });
      }
    } catch (error: any) {
      console.error("Error creating map", error);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.message ||
            "Failed to create map. Please check the details and try again.",
          type: "error",
        })
      );
    } finally {
      setIsCreating(false);
    }
  };

  const selectedInterfaceValue = selectedInterface
    ? interfaceData?.find(
        (item: any) =>
          item.affiliate_system_interface_name === selectedInterface
      )
      ? {
          label: selectedInterface,
          id: interfaceData.find(
            (item: any) =>
              item.affiliate_system_interface_name === selectedInterface
          ).id,
        }
      : null
    : null;

  const selectedDestinationTableValue = selectedDestinationTable
    ? destinationTablesData?.find(
        (item: any) => item.destination_table === selectedDestinationTable
      )
      ? {
          label: selectedDestinationTable,
          id: destinationTablesData.find(
            (item: any) => item.destination_table === selectedDestinationTable
          ).id,
        }
      : null
    : null;

  useEffect(() => {
    return () => {
      if (!navigatingBack) return;
      if (originalParams) {
        navigate(`/data_collection/unifier/maps?${originalParams}`, {
          replace: true,
        });
      } else {
        navigate(`/data_collection/unifier/maps`, { replace: true });
      }
      dispatch(setOriginalParams(""));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Paper elevation={0} sx={{ padding: "12px 16px", mb: "16px" }}>
            <Grid container alignItems="center" columnSpacing={3}>
              <Grid size={12} mb={2}>
                <Typography variant="h3" sx={{ fontSize: "24px" }}>
                  Create New Map
                </Typography>
              </Grid>
              <Grid size={9}>
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  justifyContent={"flex-end"}
                  gap={2}
                >
                  <TextField
                    required
                    label="Map Name"
                    placeholder="Please enter map name"
                    color="secondary"
                    size="small"
                    fullWidth
                    value={mapName}
                    onChange={(e) => setMapName(e.target.value)}
                    onBlur={() =>
                      setMapName((prev) => (prev ? prev.trim() : prev))
                    }
                    inputProps={{ maxLength: 255 }}
                  />
                  <Autocomplete
                    sx={{ width: "100%" }}
                    options={
                      interfaceData?.map((item: any) => ({
                        label: item.affiliate_system_interface_name,
                        id: item.id,
                      })) || []
                    }
                    value={selectedInterfaceValue}
                    onChange={(_event, newValue: any) => {
                      setSelectedInterface(newValue?.label);
                      dispatch(setSelectedInterfaceIDMaps(newValue?.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose from the list"
                        label="Interface"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />
                  <Autocomplete
                    sx={{ width: "100%" }}
                    options={
                      destinationTablesData
                        ?.filter(
                          (item: any) =>
                            item.destination_table !==
                            "daily_brand_campaign_performance"
                        )
                        .map((item: any) => ({
                          label: item.destination_table,
                          id: item.id,
                        })) || []
                    }
                    value={selectedDestinationTableValue}
                    onChange={(_event, newValue: any) => {
                      setSelectedDestinationTable(newValue?.label);
                      dispatch(setSelectedDestinationTableIDMaps(newValue?.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose from the list"
                        label="Destination Table"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />
                </Stack>
              </Grid>
              <Grid size={3} textAlign={"right"}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCreateMap}
                  disabled={
                    !mapName ||
                    !selectedInterface ||
                    !selectedDestinationTable ||
                    !isValidPayloadDetails
                  }
                  sx={{
                    padding: "8px 20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Create Map
                  {isCreating && <SimpleBackdrop />}
                  <AddIcon sx={{ width: "20px", ml: "5px" }} />
                </Button>
              </Grid>
            </Grid>
          </Paper>
          {/* Create Map Details Table*/}
          {mapName.trim() && selectedInterface && selectedDestinationTable ? (
            <CreateMapDetails
              tableHeadings={tableHeadings}
              selectedInterface={selectedInterface}
              selectedDestinationTable={selectedDestinationTable}
            />
          ) : null}
        </>
      )}
    </>
  );
};

export default CreateMap;
